import { LatLon as UtmLatLon } from 'geodesy/utm';
import { Berth } from 'src/interfaces/PortData';
import { Point2D, VesselScenarioContainer } from "src/interfaces/VesselScenario";
import { AppStateStore, PortStore } from 'src/stores';
import { AllBollard, CrossAwarenessScenario, VisibleScenario } from "src/stores/types";
import { getFullDeckPlane } from './ma/calculateConnectivity';
import { getFairleadToBollardLines, getHeadingAngleTN, getVesselCentreOnVessel } from "./mapHelpers";

interface ClosestPolygonPointsResult {
  distance: number;
  pointOnPolygon1: Point2D;
  pointOnPolygon2: Point2D;
}

/**
 * Calculates the smallest angle difference between two angles, handling wrap-around (e.g., 359° vs 1° = 2°)
 */
function getAngleDifference(a: number, b: number): number {
  const diff = Math.abs(a - b) % 360;
  return diff > 180 ? 360 - diff : diff;
}

// Rotate and translate the deck plane points to be in the primary vessel's local coordinate system
function rotateAndTranslateDeckPlane(
  deckPlane: Point2D[],
  rotationAngle: number,
  distanceX: number,
  distanceY: number
): Point2D[] {
  // Convert heading angle to radians
  const headingRad = (rotationAngle * Math.PI) / 180;
  // Transform the deck plane points
  return deckPlane.map(point => {
    // Rotate the point using standard 2D rotation matrix
    const rotatedX = point.x * Math.cos(headingRad) - point.y * Math.sin(headingRad);
    const rotatedY = point.x * Math.sin(headingRad) + point.y * Math.cos(headingRad);

    // Translate the point
    const translatedX = rotatedX + distanceX;
    const translatedY = rotatedY + distanceY;

    return { x: translatedX, y: translatedY };
  }
  );
}

/**
 * Gets the primary vessel's deck plane in its local coordinate system (UTM, centered at vessel center)
 *
 * @param primaryVesselScenario - The primary vessel scenario
 * @param primaryBerth - The primary vessel's berth
 * @returns The deck plane in local coordinates
 */
function getPrimaryVesselDeckPlaneInLocalCoordinates(
  primaryVesselScenario: VisibleScenario,
  primaryBerth: Berth
): Point2D[] {
  const primaryVesselDeckPlane = primaryVesselScenario.data.vessel.deckPlane;
  const fullDeckPlane = getFullDeckPlane(primaryVesselDeckPlane, primaryVesselScenario.data.vessel.beam);

  // The primary vessel's deck plane is centered at origin, and already aligned with ship local rotation: 
  return rotateAndTranslateDeckPlane(fullDeckPlane, 0, 0, 0);
}

/**
 * Calculates the offset and relative heading of a secondary vessel relative to a primary vessel
 *
 * @param primaryVesselCentre - The primary vessel's center coordinates
 * @param primaryHeadingAngle - The primary vessel heading angle in degrees
 * @param secondaryVesselScenario - The secondary vessel scenario
 * @param secondaryBerth - The berth information for the secondary vessel
 * @returns Object containing distanceX, distanceY (in primary vessel's local coordinates), and relativeHeading
 */
function calculateSecondaryVesselOffset(
  primaryVesselCentre: UtmLatLon,
  primaryHeadingAngle: number,
  secondaryVesselScenario: CrossAwarenessScenario,
  secondaryBerth: Berth
): { distanceX: number; distanceY: number; relativeHeading: number; secondaryHeadingAngle: number; } {
  // Get berth and heading information for secondary vessel
  const secondaryHeadingAngle = getHeadingAngleTN(secondaryVesselScenario.data.mooring, secondaryBerth);
  const secondaryVesselCentre = getVesselCentreOnVessel(
    secondaryBerth,
    secondaryVesselScenario.data.mooring,
    secondaryVesselScenario.data.vessel,
    0
  );

  const averageZone = primaryVesselCentre.toUtm().zone;
  const primaryVesselCentreLocal = primaryVesselCentre.toUtm(averageZone);
  const secondaryVesselCentreLocal = secondaryVesselCentre.toUtm(averageZone);

  // Calculate distance in global UTM coordinates
  const globalDistanceX = secondaryVesselCentreLocal.easting - primaryVesselCentreLocal.easting;
  const globalDistanceY = secondaryVesselCentreLocal.northing - primaryVesselCentreLocal.northing;

  // Rotate the distance vector into the primary vessel's local coordinate system
  const primaryHeadingRad = ((primaryHeadingAngle) * Math.PI) / 180;
  const distanceX = globalDistanceY * Math.cos(primaryHeadingRad) + globalDistanceX * Math.sin(primaryHeadingRad);
  const distanceY = globalDistanceY * Math.sin(primaryHeadingRad) - globalDistanceX * Math.cos(primaryHeadingRad);
  // Calculate relative heading (secondary heading relative to primary)
  const relativeHeading = -secondaryHeadingAngle + primaryHeadingAngle;

  return { distanceX, distanceY, relativeHeading, secondaryHeadingAngle };
}

/**
 * Transforms the secondary vessel's deck plane to the primary vessel's local coordinate system
 *
 * @param secondaryVesselScenario - The secondary vessel scenario
 * @param distanceX - The X offset in the primary vessel's local coordinate system
 * @param distanceY - The Y offset in the primary vessel's local coordinate system
 * @param relativeHeading - The relative heading between vessels (secondary - primary)
 * @returns The transformed deck plane in the primary vessel's local coordinate system
 */
function getSecondaryVesselDeckPlaneInLocalCoordinates(
  secondaryVesselScenario: CrossAwarenessScenario,
  distanceX: number,
  distanceY: number,
  relativeHeading: number
): Point2D[] {
  // Get the deck plane of the secondary vessel
  const secondaryVesselDeckPlane = secondaryVesselScenario.data.vessel.deckPlane;

  // Transform secondary vessel's deck plane to primary vessel's coordinates:
  return rotateAndTranslateDeckPlane(
    getFullDeckPlane(secondaryVesselDeckPlane, secondaryVesselScenario.data.vessel.beam),
    relativeHeading,
    distanceX,
    distanceY
  );
}

/**
 * Calculate the local coordinates of the lines connecting the fairlead to the bollards
 * Transforms mooring lines from global UTM coordinates to the vessel's local coordinate system
 *
 * @param appStateStore - The app state store instance
 * @param portStore - The port store instance
 * @param bollards - All available bollards
 * @param vesselCentre - The vessel's center coordinates
 * @param vesselScenario - The vessel scenario
 * @param berth - The berth information
 * @param headingAngle - The vessel's heading angle in degrees
 * @param utmZone - The UTM zone to use for coordinate conversion
 * @returns Array of line objects with coordinates in the vessel's local coordinate system
 */
function getLineCoordinatesInLocal(
  appStateStore: AppStateStore,
  portStore: PortStore,
  bollards: Partial<AllBollard>[],
  vesselCentre: UtmLatLon,
  vesselScenario: VisibleScenario | CrossAwarenessScenario,
  berth: Berth,
  headingAngle: number,
  utmZone: string,
) {

  const fairleadToBollardLines = getFairleadToBollardLines(
    appStateStore,
    vesselScenario as VesselScenarioContainer,
    portStore,
    berth,
    vesselScenario.data.vessel,
    vesselScenario.data.mooring,
    bollards
  );

  const vesselCentreLocal = vesselCentre.toUtm(utmZone);

  // Convert heading angle to radians for rotation
  const headingRad = (headingAngle * Math.PI) / 180;

  // Loop over the mooring lines:
  const linesLocalCoordinates = fairleadToBollardLines.map((line, index) => {

    const lineCoordinates: Point2D[] = [];

    line.coordinate.forEach((coordinate) => {
      const coordinateLocal = new UtmLatLon(coordinate[1], coordinate[0]).toUtm(utmZone);

      // First, get coordinates relative to vessel center in global orientation
      const relativeX = coordinateLocal.easting - vesselCentreLocal.easting;
      const relativeY = coordinateLocal.northing - vesselCentreLocal.northing;

      // Rotate into vessel's local coordinate system
      const lineCoordinate: Point2D = {
        x: relativeY * Math.cos(headingRad) + relativeX * Math.sin(headingRad),
        y: relativeY * Math.sin(headingRad) - relativeX * Math.cos(headingRad)
      };
      lineCoordinates.push(lineCoordinate);
    });

    return {
      lineId: index,
      lineName: line?.properties?.name ?? `Line ${index + 1}`,
      lineCoordinates,
    };
  })


  return linesLocalCoordinates;
}

/**
 * Gets the secondary vessel's mooring lines transformed to the primary vessel's local coordinate system
 *
 * @param appStateStore - The app state store instance
 * @param portStore - The port store instance
 * @param bollards - All available bollards
 * @param secondaryVesselScenario - The secondary vessel scenario
 * @param secondaryBerth - The berth information for the secondary vessel
 * @param distanceX - The X offset in the primary vessel's local coordinate system
 * @param distanceY - The Y offset in the primary vessel's local coordinate system
 * @param secondaryVesselHeading - The secondary vessel's heading angle in degrees
 * @param relativeHeading - The relative heading between vessels (secondary - primary)
 * @param utmZone - The UTM zone to use for coordinate conversion
 * @returns Array of line objects with coordinates in the primary vessel's local coordinate system
 */
function getSecondaryVesselLinesInLocalCoordinates(
  appStateStore: AppStateStore,
  portStore: PortStore,
  bollards: Partial<AllBollard>[],
  secondaryVesselScenario: CrossAwarenessScenario,
  secondaryBerth: Berth,
  distanceX: number,
  distanceY: number,
  secondaryVesselHeading: number,
  relativeHeading: number,
  utmZone: string,
) {
  // Get the secondary vessel's fairlead to bollard lines
  const fairleadToBollardLines = getFairleadToBollardLines(
    appStateStore,
    secondaryVesselScenario as VesselScenarioContainer,
    portStore,
    secondaryBerth,
    secondaryVesselScenario.data.vessel,
    secondaryVesselScenario.data.mooring,
    bollards
  );

  // Get the secondary vessel's center
  const secondaryVesselCentre = getVesselCentreOnVessel(
    secondaryBerth,
    secondaryVesselScenario.data.mooring,
    secondaryVesselScenario.data.vessel,
    0
  );
  const secondaryVesselCentreLocal = secondaryVesselCentre.toUtm(utmZone);

  // Convert secondary vessel heading to radians
  const secondaryHeadingRad = (secondaryVesselHeading * Math.PI) / 180;
  const relativeHeadingRad = (relativeHeading * Math.PI) / 180;

  // Transform each mooring line
  const linesLocalCoordinates = fairleadToBollardLines.map((line, index) => {
    const lineCoordinates: Point2D[] = [];

    line.coordinate.forEach((coordinate) => {
      // Convert to UTM
      const coordinateLocal = new UtmLatLon(coordinate[1], coordinate[0]).toUtm(utmZone);

      // First, get coordinates relative to secondary vessel's center in global UTM
      const relativeX = coordinateLocal.easting - secondaryVesselCentreLocal.easting;
      const relativeY = coordinateLocal.northing - secondaryVesselCentreLocal.northing;

      // Rotate into secondary vessel's local coordinate system
      const secondaryLocalX = relativeY * Math.cos(secondaryHeadingRad) + relativeX * Math.sin(secondaryHeadingRad);
      const secondaryLocalY = relativeY * Math.sin(secondaryHeadingRad) - relativeX * Math.cos(secondaryHeadingRad);

      // Transform from secondary vessel's local coords to primary vessel's local coords
      const lineCoordinate: Point2D = {
        x: secondaryLocalX * Math.cos(relativeHeadingRad) - secondaryLocalY * Math.sin(relativeHeadingRad) + distanceX,
        y: secondaryLocalX * Math.sin(relativeHeadingRad) + secondaryLocalY * Math.cos(relativeHeadingRad) + distanceY
      };

      lineCoordinates.push(lineCoordinate);
    });

    return {
      lineId: index,
      lineName: line?.properties?.name ?? `Line ${index + 1}`,
      lineCoordinates,
    };
  });

  return linesLocalCoordinates;
}

/**
 * Calculates the closest point on a line segment to a given point, along with the distance
 */
function pointToSegmentClosestPoint(point: Point2D, segmentStart: Point2D, segmentEnd: Point2D): { distance: number; closestPoint: Point2D } {
  const dx = segmentEnd.x - segmentStart.x;
  const dy = segmentEnd.y - segmentStart.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    const pdx = point.x - segmentStart.x;
    const pdy = point.y - segmentStart.y;
    return { distance: Math.sqrt(pdx * pdx + pdy * pdy), closestPoint: { x: segmentStart.x, y: segmentStart.y } };
  }

  const t = Math.max(0, Math.min(1,
    ((point.x - segmentStart.x) * dx + (point.y - segmentStart.y) * dy) / lengthSquared
  ));

  const closestPoint = {
    x: segmentStart.x + t * dx,
    y: segmentStart.y + t * dy
  };

  const distX = point.x - closestPoint.x;
  const distY = point.y - closestPoint.y;
  return { distance: Math.sqrt(distX * distX + distY * distY), closestPoint };
}

/**
 * Calculates the distance from a point to a line segment
 * @param point The point to measure from
 * @param segmentStart Start point of the line segment
 * @param segmentEnd End point of the line segment
 * @returns The minimum distance from the point to the segment
 */
function pointToSegmentDistance(point: Point2D, segmentStart: Point2D, segmentEnd: Point2D): number {
  const dx = segmentEnd.x - segmentStart.x;
  const dy = segmentEnd.y - segmentStart.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    // Segment is a point
    const pdx = point.x - segmentStart.x;
    const pdy = point.y - segmentStart.y;
    return Math.sqrt(pdx * pdx + pdy * pdy);
  }

  // Calculate the projection parameter t
  const t = Math.max(0, Math.min(1,
    ((point.x - segmentStart.x) * dx + (point.y - segmentStart.y) * dy) / lengthSquared
  ));

  // Find the closest point on the segment
  const closestX = segmentStart.x + t * dx;
  const closestY = segmentStart.y + t * dy;

  // Return the distance to the closest point
  const distX = point.x - closestX;
  const distY = point.y - closestY;
  return Math.sqrt(distX * distX + distY * distY);
}

/**
 * Calculates the minimum distance between two line segments
 * @param p1 First point of first segment
 * @param p2 Second point of first segment
 * @param p3 First point of second segment
 * @param p4 Second point of second segment
 * @returns The minimum distance between the two segments
 */
function segmentToSegmentDistance(p1: Point2D, p2: Point2D, p3: Point2D, p4: Point2D): number {
  // Check if segments intersect
  if (doLineSegmentsIntersect(p1, p2, p3, p4)) {
    return 0;
  }

  // Calculate minimum distance from each endpoint to the other segment
  return Math.min(
    pointToSegmentDistance(p1, p3, p4),
    pointToSegmentDistance(p2, p3, p4),
    pointToSegmentDistance(p3, p1, p2),
    pointToSegmentDistance(p4, p1, p2)
  );
}

/**
 * Calculates the minimum distance between two polygons defined by arrays of points
 * @param polygon1 Array of points defining the first polygon
 * @param polygon2 Array of points defining the second polygon
 * @returns The minimum distance between the two polygons (0 if they intersect)
 */
function getMinDistanceBetweenPolygons(polygon1: Point2D[], polygon2: Point2D[]): number {
  if (polygon1.length < 2 || polygon2.length < 2) {
    return Infinity;
  }

  let minDistance = Infinity;

  // Compare each edge of polygon1 with each edge of polygon2
  for (let i = 0; i < polygon1.length; i++) {
    const p1 = polygon1[i];
    const p2 = polygon1[(i + 1) % polygon1.length];

    for (let j = 0; j < polygon2.length; j++) {
      const p3 = polygon2[j];
      const p4 = polygon2[(j + 1) % polygon2.length];

      const distance = segmentToSegmentDistance(p1, p2, p3, p4);
      if (distance < minDistance) {
        minDistance = distance;
        if (minDistance === 0) {
          return 0; // Early exit if polygons intersect
        }
      }
    }
  }

  return minDistance;
}

/**
 * Calculates the minimum distance between two polygons and returns the closest points on each polygon
 */
function getMinDistanceBetweenPolygonsWithClosestPoints(polygon1: Point2D[], polygon2: Point2D[]): ClosestPolygonPointsResult {
  if (polygon1.length < 2 || polygon2.length < 2) {
    return { distance: Infinity, pointOnPolygon1: { x: 0, y: 0 }, pointOnPolygon2: { x: 0, y: 0 } };
  }

  let minDistance = Infinity;
  let bestPointOnPoly1: Point2D = polygon1[0];
  let bestPointOnPoly2: Point2D = polygon2[0];

  for (let i = 0; i < polygon1.length; i++) {
    const p1 = polygon1[i];
    const p2 = polygon1[(i + 1) % polygon1.length];

    for (let j = 0; j < polygon2.length; j++) {
      const p3 = polygon2[j];
      const p4 = polygon2[(j + 1) % polygon2.length];

      if (doLineSegmentsIntersect(p1, p2, p3, p4)) {
        return { distance: 0, pointOnPolygon1: p1, pointOnPolygon2: p3 };
      }

      const check1 = pointToSegmentClosestPoint(p1, p3, p4);
      if (check1.distance < minDistance) {
        minDistance = check1.distance;
        bestPointOnPoly1 = p1;
        bestPointOnPoly2 = check1.closestPoint;
      }

      const check2 = pointToSegmentClosestPoint(p2, p3, p4);
      if (check2.distance < minDistance) {
        minDistance = check2.distance;
        bestPointOnPoly1 = p2;
        bestPointOnPoly2 = check2.closestPoint;
      }

      const check3 = pointToSegmentClosestPoint(p3, p1, p2);
      if (check3.distance < minDistance) {
        minDistance = check3.distance;
        bestPointOnPoly1 = check3.closestPoint;
        bestPointOnPoly2 = p3;
      }

      const check4 = pointToSegmentClosestPoint(p4, p1, p2);
      if (check4.distance < minDistance) {
        minDistance = check4.distance;
        bestPointOnPoly1 = check4.closestPoint;
        bestPointOnPoly2 = p4;
      }
    }
  }

  return { distance: minDistance, pointOnPolygon1: bestPointOnPoly1, pointOnPolygon2: bestPointOnPoly2 };
}

/**
 * Determines if two line segments intersect
 * @param p1 First point of first line segment
 * @param p2 Second point of first line segment
 * @param p3 First point of second line segment
 * @param p4 Second point of second line segment
 * @returns boolean indicating if the segments intersect
 */
function doLineSegmentsIntersect(p1: Point2D, p2: Point2D, p3: Point2D, p4: Point2D): boolean {
  // Calculate directions
  const d1x = p2.x - p1.x;
  const d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x;
  const d2y = p4.y - p3.y;

  // Calculate the determinant
  const determinant = d1x * d2y - d1y * d2x;

  // If determinant is zero, lines are parallel or collinear
  if (determinant === 0) return false;

  const dx = p3.x - p1.x;
  const dy = p3.y - p1.y;

  // Calculate parameters
  const t = (dx * d2y - dy * d2x) / determinant;
  const u = (dx * d1y - dy * d1x) / determinant;

  // Check if intersection point is within both line segments
  return (t >= 0 && t <= 1 && u >= 0 && u <= 1);
}

/**
 * Checks if two line arrays intersect with each other
 * @param lineArray1 First array of points representing connected line segments
 * @param lineArray2 Second array of points representing connected line segments
 * @returns boolean indicating if any line segments from the two arrays intersect
 */
function doLineArraysIntersect(lineArray1: Point2D[], lineArray2: Point2D[]): boolean {
  // Check if either array has fewer than 2 points (no line segments)
  if (lineArray1.length < 2 || lineArray2.length < 2) {
    return false;
  }

  // Check each line segment in the first array against each in the second
  for (let i = 0; i < lineArray1.length - 1; i++) {
    const p1 = lineArray1[i];
    const p2 = lineArray1[i + 1];

    for (let j = 0; j < lineArray2.length - 1; j++) {
      const p3 = lineArray2[j];
      const p4 = lineArray2[j + 1];

      if (doLineSegmentsIntersect(p1, p2, p3, p4)) {
        return true;
      }
    }
  }

  return false;
}

export {
  calculateSecondaryVesselOffset,
  doLineArraysIntersect,
  getAngleDifference,
  getLineCoordinatesInLocal, getMinDistanceBetweenPolygons,
  getMinDistanceBetweenPolygonsWithClosestPoints,
  getPrimaryVesselDeckPlaneInLocalCoordinates,
  getSecondaryVesselDeckPlaneInLocalCoordinates,
  getSecondaryVesselLinesInLocalCoordinates
};

